import { LocalizedString, PaymentProviderType, PriceInCurrency, ServiceFeeType } from "../general/models";
import { AmountInCurrency } from "../coupons/models";


export interface CloudFiscalProvider {
    type: string;
}


export interface OrganizationBranchDocs {
    menuPublicOfferUrl: string | null;
}

export interface ServiceFee {
    isEnabled: boolean;
    title: { [key: string]: string };
    type: ServiceFeeType;
}

export type ServiceFees = Fixed;

interface Fixed extends ServiceFee {
    amount: AmountInCurrency;
    isEnabled: boolean;
    title: { [key: string]: string };
}

export interface IOfflinePaymentSettings{
    isEnabled: boolean,
    type: PaymentProviderType.offline,
    serviceFee: Fixed,
    externalId: string | null
}

export interface ICloudPaymentsPaymentSettings{
    isEnabled: boolean,
    type: PaymentProviderType.cloudpayments,
    serviceFee: Fixed;
    externalId: string | null
}

export interface IToBankCardPaymentSettings{
    isEnabled: boolean,
    type: PaymentProviderType.toBankCard,
    requisites: string,
    title: LocalizedString,
    description: LocalizedString,
    buttonTitle: LocalizedString,
    buttonUrl: string,
    serviceFee: Fixed
}


export interface ICompanyInvoiceSettings{
    isEnabled: boolean,
    type: PaymentProviderType.companyInvoice,
    serviceFee: Fixed
}


export interface ISBPTochkaPaymentSettings{
    isEnabled: boolean,
    type: PaymentProviderType.sbpTochka,
    serviceFee: Fixed;
    externalId: string | null
}
export interface ISberbankPaymentSettings{
    isEnabled: boolean,
    type: PaymentProviderType.sberbank,
    serviceFee: Fixed;
    externalId: string | null
}


export type PaymentSettings =  IOfflinePaymentSettings | ICloudPaymentsPaymentSettings
    | IToBankCardPaymentSettings | ICompanyInvoiceSettings | ISBPTochkaPaymentSettings | ISberbankPaymentSettings



export interface ReportingDestination {
    type: string;
}

export interface Reporting {
    destinations: ReportingDestination[];
}


export interface OrganizationData {
    name: string;
}

export interface Organization {
    data: OrganizationData;
    id: number;
}


interface PaymentProvider {
    type: PaymentProviderType;
}


export interface OrganizationBranchData {
    defaultCurrencyId: string;
    defaultLocaleId: string;
    defaultMenuId: number | null;
    docs: OrganizationBranchDocs;
    linkedWarehousesIds: number[];
    name: string;
    paymentSettings: { [key: string]: PaymentSettings };
    reporting: Reporting;
}

export interface CloudPaymentsPaymentProviderDetails {
    branchName: string;
    publicId: string;
}

export interface OrganizationBranchPaymentProviderDetails {
    cloudPaymentsCheckout: CloudPaymentsPaymentProviderDetails;
}

export interface CardServiceOrder {
    organizationId: number
    organizationBranchId: number
    type: 'Card'
    tariff: 'Simple' | 'Basic' | 'Advanced'
    period: 'Month' | 'HalfYear' | 'Year'
    price: PriceInCurrency
    status: 'New' | 'Cancelled' | 'Paid' | 'Error'
    createdAt: Date
    paidAt: Date | null
    cancelledAt: Date | null
    orderId: number
    receiptLink: string | null
    refundReceiptLink: string | null
    transactionId: string | null
}

export interface InvoiceServiceOrder {
    organizationId: number
    organizationBranchId: number
    type: 'Invoice'
    tariff: 'Simple' | 'Basic' | 'Advanced'
    period: 'Month' | 'HalfYear' | 'Year'
    price: PriceInCurrency
    status: 'New' | 'Cancelled' | 'Paid' | 'Error'
    createdAt: Date
    paidAt: Date | null
    cancelledAt: Date | null
    invoiceId: string
    invoiceLink: string | null
}

export interface TariffDetails {
    monthlySubscriptionFee: PriceInCurrency,
    paymentProviderTransactionFeePercent: number
    restifyTransactionFeePercent: number
    type: string,
    nextPaymentDate: Date,
    menusActiveTillDate: Date,
    availableMenusCount: number,
    isOrdersEnabled: boolean,
    isPaymentsEnabled: boolean
}

export interface OrganizationBranch {
    cloudFiscalProviders: { [key: string]: CloudFiscalProvider };
    companyEntityData: CompanyEntityData;
    data: OrganizationBranchData;
    id: number;
    organizationId: number;
    paymentProviders: { [key: string]: PaymentProvider };
    privateDocs: {
        ofertaUrl: string
        personalTermsUrl: string
    },
    activeTariffDetails: TariffDetails,
    legalData: CompanyCard | null
}

export interface CompanyEntityData {
    companyCard: CompanyCard | null;
    isCompanyCardLocked: boolean;
}
export type CompanyCard = RussianOOOCard | RussianIPCard;

export enum CompanyCardTypes {
    RussianOOOCard = "RussianOOOCard",
    RussianIPCard = "RussianIPCard"
}

export  interface RussianOOOCard {
    type: CompanyCardTypes.RussianOOOCard;
    accountManager: LocalizedString;
    accountManagerSignatureUrl: string;
    bankBik: string;
    bankCorrespondentAccountNumber: string;
    bankCurrentAccountNumber: string;
    bankLocation: LocalizedString;
    bankTitle: LocalizedString;
    description: LocalizedString;
    director: LocalizedString;
    directorSignatureUrl: string;
    fullTitle: LocalizedString;
    inn: string;
    kpp: string;
    legalAddress: LocalizedString;
    logoUrl: string;
    ogrn: string;
    phoneNumber: string;
    printUrl: string;
    shortTitle: LocalizedString;
}

export interface RussianIPCard {
    type: CompanyCardTypes.RussianIPCard;
    accountManager: LocalizedString;
    accountManagerSignatureUrl: string;
    bankBik: string;
    bankCorrespondentAccountNumber: string;
    bankCurrentAccountNumber: string;
    bankLocation: LocalizedString;
    bankTitle: LocalizedString;
    description: LocalizedString;
    director: LocalizedString;
    directorSignatureUrl: string;
    fullTitle: LocalizedString;
    inn: string;
    legalAddress: LocalizedString;
    logoUrl: string;
    ogrnip: string;
    phoneNumber: string;
    printUrl: string;
    shortTitle: LocalizedString;
}

export interface OrganizationBranchSimplifiedPublic {
    data: OrganizationBranchData;
    id: number;
    organizationId: number;
    activeTariffDetails: TariffDetails
}
